import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { Toaster } from "./components/ui/sonner";
import { AuthProvider, useAuth } from "./context/AuthContext";
import ChatWidget from "./components/ChatWidget";
import { Phone, Car } from "lucide-react";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { startVisitorTracking } from "./utils/visitorTracking";
import { initTracking, trackPageView } from "./utils/analytics";
import { hasAnalyticsConsent } from "./utils/cookieConsent";
import CookieConsent from "./components/CookieConsent";
import ExitIntentPopup from "./components/ExitIntentPopup";
import ConciergeChat from "./components/ConciergeChat";

// Handles redirects from shared links and 404 fallback (e.g. /?go=/vehicle/abc or /?go=%2Fvehicle%2Fabc)
const SharedLinkRedirect = () => {
  const location = useLocation();
  const navigate = useNavigate();
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const goTo = params.get('go');
    if (goTo) {
      const decoded = decodeURIComponent(goTo);
      if (decoded.startsWith('/')) {
        navigate(decoded, { replace: true });
      }
    }
  }, [location.search, navigate]);
  return null;
};

// SEO-friendly vehicle URL redirect - /vehicule/2024-kia-forte-lx -> /vehicle/{uuid}
const VehicleSlugRedirect = () => {
  const { slug } = require('react-router-dom').useParams();
  const navigate = require('react-router-dom').useNavigate();
  const API = process.env.REACT_APP_BACKEND_URL;
  
  require('react').useEffect(() => {
    const resolve = async () => {
      try {
        const res = await fetch(`${API}/api/vehicles/by-slug/${slug}`);
        if (res.ok) {
          const data = await res.json();
          navigate(`/vehicle/${data.vehicle_id}`, { replace: true });
        } else {
          navigate('/inventory', { replace: true });
        }
      } catch {
        navigate('/inventory', { replace: true });
      }
    };
    resolve();
  }, [slug, navigate, API]);
  
  return <div className="min-h-screen flex items-center justify-center"><div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600" /></div>;
};

// Pages
import HomePage from "./pages/HomePage";
import InventoryPage from "./pages/InventoryPage";
import VehicleDetailPage from "./pages/VehicleDetailPage";
import ContactPage from "./pages/ContactPage";
import TeamPage from "./pages/TeamPage";
import FinancingPage from "./pages/FinancingPage";
import WarrantyPage from "./pages/WarrantyPage";
import CreditApplicationPage from "./pages/CreditApplicationPage";
import SellVehiclePage from "./pages/SellVehiclePage";
import LoginPage from "./pages/LoginPage";
import PrivacyPolicyPage from "./pages/PrivacyPolicyPage";
import AdminDashboard from "./pages/admin/AdminDashboard";
import AdminVehicles from "./pages/admin/AdminVehicles";
import AdminInquiries from "./pages/admin/AdminInquiries";
import AdminMarketing from "./pages/admin/AdminMarketing";
import AdminChatbot from "./pages/admin/AdminChatbot";
import AdminTestDrives from "./pages/admin/AdminTestDrives";
import AdminAnalytics from "./pages/admin/AdminAnalytics";
import AdminExports from "./pages/admin/AdminExports";
import AdminPricing from "./pages/admin/AdminPricing";
import AdminSync from "./pages/admin/AdminSync";
import AdminMobilePhotos from "./pages/admin/AdminMobilePhotos";
import AdminPromos from "./pages/admin/AdminPromos";
import AdminCustomerPhotos from "./pages/admin/AdminCustomerPhotos";
import AdminAccessCodes from "./pages/admin/AdminAccessCodes";
import AdminFBPosts from "./pages/admin/AdminFBPosts";
import AdminAppraisals from "./pages/admin/AdminAppraisals";
import AdminFBMarketplace from "./pages/admin/AdminFBMarketplace";
import AdminSEM from "./pages/admin/AdminSEM";
import MobileUpload from "./pages/MobileUpload";
import BlogPage from "./pages/BlogPage";
import BlogPostPage from "./pages/BlogPostPage";
import RecentDeliveriesPage from "./pages/RecentDeliveriesPage";
import NotreProcessusPage from "./pages/NotreProcessusPage";
import SeoLandingPage from "./pages/SeoLandingPage";
import TradeInPage from "./pages/TradeInPage";
import ReferralPage from "./pages/ReferralPage";
import ReservationSuccessPage from "./pages/ReservationSuccessPage";
import AdminCarvana from "./pages/admin/AdminCarvana";

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  return children;
};

// Chat widget wrapper - only show on public pages
const ChatWidgetWrapper = () => {
  const location = useLocation();
  const isAdminPage = location.pathname.startsWith('/admin') || location.pathname === '/login' || location.pathname === '/upload';
  
  if (isAdminPage) return null;
  return (
    <>
      <ChatWidget />
      <ConciergeChat />
    </>
  );
};

// Visitor tracking wrapper - track on public pages (gated by cookie consent)
const VisitorTracker = () => {
  const location = useLocation();
  
  useEffect(() => {
    const isAdminPage = location.pathname.startsWith('/admin') || location.pathname === '/login';
    if (!isAdminPage && hasAnalyticsConsent()) {
      startVisitorTracking();
      initTracking();
    }

    // Re-check consent when user updates preferences
    const handleConsentUpdate = (e) => {
      if (e.detail?.analytics) {
        startVisitorTracking();
        initTracking();
      }
    };
    window.addEventListener('cookie-consent-updated', handleConsentUpdate);
    return () => window.removeEventListener('cookie-consent-updated', handleConsentUpdate);
  }, []);
  
  // Track page views on navigation (only if consented)
  useEffect(() => {
    const isAdminPage = location.pathname.startsWith('/admin') || location.pathname === '/login';
    if (!isAdminPage && hasAnalyticsConsent()) {
      trackPageView(location.pathname);
    }
  }, [location.pathname]);
  
  return null;
};

// Mobile CTA Bar - sticky at bottom on mobile (hidden on vehicle detail page which has its own)
const MobileCTABar = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const isAdminPage = location.pathname.startsWith('/admin') || location.pathname === '/login' || location.pathname === '/upload';
  const isVehicleDetailPage = location.pathname.startsWith('/vehicle/');
  
  if (isAdminPage || isVehicleDetailPage) return null;
  
  return (
    <div className="md:hidden fixed bottom-0 left-0 right-0 z-40 bg-white border-t border-slate-200 shadow-[0_-4px_20px_rgba(0,0,0,0.1)] safe-area-bottom">
      <div className="grid grid-cols-2 gap-2 p-2.5">
        <a 
          href="tel:+18339956477"
          className="flex items-center justify-center gap-2 bg-slate-100 hover:bg-slate-200 text-slate-700 font-semibold py-3 px-4 rounded-xl transition-colors"
        >
          <Phone className="h-5 w-5" />
          <span>{t('nav.call')}</span>
        </a>
        <a 
          href="/inventory"
          className="flex items-center justify-center gap-2 bg-[#3b82f6] hover:bg-blue-600 text-white font-semibold py-3 px-4 rounded-xl transition-colors"
        >
          <Car className="h-5 w-5" />
          <span>{t('nav.inventory')}</span>
        </a>
      </div>
    </div>
  );
};

function App() {
  return (
    <HelmetProvider>
    <AuthProvider>
      <BrowserRouter>
        <SharedLinkRedirect />
        <Routes>
          {/* Public Routes */}
          <Route path="/" element={<HomePage />} />
          <Route path="/inventory" element={<InventoryPage />} />
          <Route path="/inventaire" element={<InventoryPage />} />
          <Route path="/vehicle/:id" element={<VehicleDetailPage />} />
          <Route path="/vehicule/:slug" element={<VehicleSlugRedirect />} />
          <Route path="/blog" element={<BlogPage />} />
          <Route path="/blog/:slug" element={<BlogPostPage />} />
          <Route path="/nos-livraisons" element={<RecentDeliveriesPage />} />
          <Route path="/notre-processus" element={<NotreProcessusPage />} />
          <Route path="/voitures-usagees/:slug" element={<SeoLandingPage />} />
          <Route path="/reprise" element={<TradeInPage />} />
          <Route path="/trade-in" element={<TradeInPage />} />
          <Route path="/referral" element={<ReferralPage />} />
          <Route path="/reservation/success" element={<ReservationSuccessPage />} />
          <Route path="/reservation/cancel" element={<Navigate to="/inventory" replace />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/equipe" element={<TeamPage />} />
          <Route path="/financement" element={<FinancingPage />} />
          <Route path="/garantie" element={<WarrantyPage />} />
          <Route path="/credit" element={<CreditApplicationPage />} />
          <Route path="/vendre" element={<SellVehiclePage />} />
          <Route path="/politique-de-confidentialite" element={<PrivacyPolicyPage />} />
          <Route path="/privacy" element={<PrivacyPolicyPage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/upload" element={<MobileUpload />} />
          
          {/* Admin Routes */}
          <Route path="/admin" element={<ProtectedRoute><AdminDashboard /></ProtectedRoute>} />
          <Route path="/admin/vehicles" element={<ProtectedRoute><AdminVehicles /></ProtectedRoute>} />
          <Route path="/admin/test-drives" element={<ProtectedRoute><AdminTestDrives /></ProtectedRoute>} />
          <Route path="/admin/inquiries" element={<ProtectedRoute><AdminInquiries /></ProtectedRoute>} />
          <Route path="/admin/marketing" element={<ProtectedRoute><AdminMarketing /></ProtectedRoute>} />
          <Route path="/admin/chatbot" element={<ProtectedRoute><AdminChatbot /></ProtectedRoute>} />
          <Route path="/admin/sem" element={<ProtectedRoute><AdminSEM /></ProtectedRoute>} />
          <Route path="/admin/analytics" element={<ProtectedRoute><AdminAnalytics /></ProtectedRoute>} />
          <Route path="/admin/exports" element={<ProtectedRoute><AdminExports /></ProtectedRoute>} />
          <Route path="/admin/fb-posts" element={<ProtectedRoute><AdminFBPosts /></ProtectedRoute>} />
          <Route path="/admin/pricing" element={<ProtectedRoute><AdminPricing /></ProtectedRoute>} />
          <Route path="/admin/sync" element={<ProtectedRoute><AdminSync /></ProtectedRoute>} />
          <Route path="/admin/photos" element={<ProtectedRoute><AdminMobilePhotos /></ProtectedRoute>} />
          <Route path="/admin/promos" element={<ProtectedRoute><AdminPromos /></ProtectedRoute>} />
          <Route path="/admin/customer-photos" element={<ProtectedRoute><AdminCustomerPhotos /></ProtectedRoute>} />
          <Route path="/admin/access-codes" element={<ProtectedRoute><AdminAccessCodes /></ProtectedRoute>} />
          <Route path="/admin/appraisals" element={<ProtectedRoute><AdminAppraisals /></ProtectedRoute>} />
          <Route path="/admin/fb-marketplace" element={<ProtectedRoute><AdminFBMarketplace /></ProtectedRoute>} />
          <Route path="/admin/carvana" element={<ProtectedRoute><AdminCarvana /></ProtectedRoute>} />
          
          {/* Legacy URL redirects - DealTracker format */}
          <Route path="/en/used-vehicle/*" element={<Navigate to="/inventory" replace />} />
          <Route path="/fr/used-vehicle/*" element={<Navigate to="/inventory" replace />} />
          <Route path="/en/vehicule-occasion/*" element={<Navigate to="/inventory" replace />} />
          <Route path="/fr/vehicule-occasion/*" element={<Navigate to="/inventory" replace />} />
          <Route path="/used-vehicle/*" element={<Navigate to="/inventory" replace />} />
          <Route path="/vehicule-occasion/*" element={<Navigate to="/inventory" replace />} />
          <Route path="/vehicles/*" element={<Navigate to="/inventory" replace />} />
          
          {/* Catch-all for old vehicle IDs */}
          <Route path="/:lang/used-vehicle/:make-:model-:year/:id" element={<Navigate to="/inventory" replace />} />
          
          {/* 404 catch-all */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        <VisitorTracker />
        <ChatWidgetWrapper />
        <MobileCTABar />
        <CookieConsent />
        <ExitIntentPopup />
        <Toaster position="top-right" richColors />
      </BrowserRouter>
    </AuthProvider>
  </HelmetProvider>
  );
}

export default App;
